/**
 * The synthesis kit (U17, R24, R26).
 *
 * Oscillators, filtered noise, ADSR envelopes, and a convolution reverb built
 * from a generated impulse response. Nothing is loaded; there is no audio file
 * anywhere in the repository.
 *
 * **Layering is what sells weight.** A tank shot is a low-frequency thump, a
 * mid-band crack, and a noise tail — not one oscillator. A single sine at 80 Hz
 * is a beep; the same sine under a click and a decaying hiss is a gun.
 */

export interface Envelope {
  attack: number
  decay: number
  sustain: number
  release: number
}

export const ENV_PERCUSSIVE: Envelope = { attack: 0.001, decay: 0.08, sustain: 0, release: 0.05 }
export const ENV_SOFT: Envelope = { attack: 0.02, decay: 0.1, sustain: 0.6, release: 0.2 }

/** A node graph that reports when it has finished, so it can be torn down. */
export interface Voice {
  readonly output: AudioNode
  /** Release every node. Called after the tail has decayed. */
  stop(): void
  /** When, in context time, this voice is completely silent. */
  readonly endsAt: number
}

export class Synth {
  /** Noise buffers are generated once and shared — they are the expensive bit. */
  private noiseBuffer: AudioBuffer | null = null
  private reverbBuffer: AudioBuffer | null = null

  constructor(readonly ctx: BaseAudioContext) {}

  /** White noise, one second, generated with a seeded PRNG for reproducibility. */
  noise(): AudioBuffer {
    if (this.noiseBuffer) return this.noiseBuffer
    const length = Math.floor(this.ctx.sampleRate)
    const buffer = this.ctx.createBuffer(1, length, this.ctx.sampleRate)
    const data = buffer.getChannelData(0)
    let seed = 0x9e3779b9
    for (let i = 0; i < length; i++) {
      seed = (Math.imul(seed ^ (seed >>> 15), 0x85ebca6b) ^ (seed >>> 13)) | 0
      data[i] = ((seed >>> 0) / 2147483648 - 1) * 0.9
    }
    this.noiseBuffer = buffer
    return buffer
  }

  /**
   * A short impulse response: exponentially decaying noise, mildly stereo.
   * Generated rather than sampled, so there is no room recording to ship.
   */
  impulse(seconds = 1.1, decay = 3.2): AudioBuffer {
    if (this.reverbBuffer) return this.reverbBuffer
    const length = Math.max(1, Math.floor(this.ctx.sampleRate * seconds))
    const buffer = this.ctx.createBuffer(2, length, this.ctx.sampleRate)
    let seed = 0x27d4eb2f
    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel)
      for (let i = 0; i < length; i++) {
        seed = (Math.imul(seed ^ (seed >>> 15), 0xc2b2ae35) ^ (seed >>> 13)) | 0
        const n = (seed >>> 0) / 2147483648 - 1
        data[i] = n * Math.pow(1 - i / length, decay)
      }
    }
    this.reverbBuffer = buffer
    return buffer
  }

  /** Apply an ADSR to a gain node, returning when the tail ends. */
  applyEnvelope(gain: GainNode, start: number, peak: number, env: Envelope, hold = 0): number {
    const g = gain.gain
    g.setValueAtTime(0.0001, start)
    g.exponentialRampToValueAtTime(Math.max(0.0001, peak), start + env.attack)
    const sustainLevel = Math.max(0.0001, peak * env.sustain)
    g.exponentialRampToValueAtTime(sustainLevel, start + env.attack + env.decay)
    const releaseAt = start + env.attack + env.decay + hold
    g.setValueAtTime(sustainLevel, releaseAt)
    g.exponentialRampToValueAtTime(0.0001, releaseAt + env.release)
    return releaseAt + env.release
  }

  /**
   * A single oscillator layer with a pitch sweep. The sweep is what turns a
   * tone into an impact — a flat pitch reads as a musical note, which is
   * exactly wrong for a weapon.
   */
  tone(opts: {
    type: OscillatorType
    from: number
    to?: number
    peak: number
    env: Envelope
    hold?: number
    start?: number
    destination: AudioNode
  }): number {
    const start = opts.start ?? this.ctx.currentTime
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = opts.type
    osc.frequency.setValueAtTime(opts.from, start)
    if (opts.to !== undefined && opts.to !== opts.from) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(1, opts.to),
        start + opts.env.attack + opts.env.decay + (opts.hold ?? 0),
      )
    }
    const endsAt = this.applyEnvelope(gain, start, opts.peak, opts.env, opts.hold ?? 0)
    osc.connect(gain).connect(opts.destination)
    osc.start(start)
    osc.stop(endsAt + 0.02)
    osc.onended = () => {
      osc.disconnect()
      gain.disconnect()
    }
    return endsAt
  }

  /** A filtered noise burst. The tail of every impact and the whole of a hiss. */
  noiseBurst(opts: {
    peak: number
    env: Envelope
    hold?: number
    filter?: BiquadFilterType
    frequency?: number
    q?: number
    start?: number
    destination: AudioNode
  }): number {
    const start = opts.start ?? this.ctx.currentTime
    const source = this.ctx.createBufferSource()
    source.buffer = this.noise()
    source.loop = true

    const gain = this.ctx.createGain()
    let node: AudioNode = source
    let filter: BiquadFilterNode | null = null
    if (opts.filter) {
      filter = this.ctx.createBiquadFilter()
      filter.type = opts.filter
      filter.frequency.setValueAtTime(opts.frequency ?? 1200, start)
      filter.Q.setValueAtTime(opts.q ?? 1, start)
      source.connect(filter)
      node = filter
    }

    const endsAt = this.applyEnvelope(gain, start, opts.peak, opts.env, opts.hold ?? 0)
    node.connect(gain).connect(opts.destination)
    source.start(start)
    source.stop(endsAt + 0.02)
    source.onended = () => {
      source.disconnect()
      filter?.disconnect()
      gain.disconnect()
    }
    return endsAt
  }
}
